import { createBrowserRouter, RouteObject } from 'react-router-dom';
import { AuthGuard } from '@lucid/auth';
import LayoutWrapper from './components/LayoutWrapper/LayoutWrapper';
import Dashboard from './pages/Dashboard/Dashboard';
import LoginPage from './pages/Login/LoginPage';
import AcceptInvitation from './pages/AcceptInvitation/AcceptInvitation';
import { InvitationHandler } from './components/InvitationHandler/InvitationHandler';
import { isPageEnabled } from './config/featureFlags';

// Admin
import WorkspaceAccess from './pages/Admin/WorkspaceAccess/WorkspaceAccess';
import PortalSetup from './pages/Admin/PortalSetup/PortalSetup';
import TestDriveScheduler from './pages/Admin/TestDriveScheduler/TestDriveScheduler';
import UserManagement from './pages/Admin/UserManagement/UserManagement';

// Prospect Desk
import Prospects from './pages/ProspectDesk/Prospects/Prospects';
import CreateNewProspect from './pages/ProspectDesk/CreateNewProspect/CreateNewProspect';
import TestDriveBookings from './pages/ProspectDesk/TestDriveBookings/TestDriveBookings';
import TestDriveCalendar from './pages/ProspectDesk/TestDriveCalendar/TestDriveCalendar';

// Order Hub
import OrderBuilder from './pages/OrderHub/OrderBuilder/OrderBuilder';
import Checkout from './pages/OrderHub/OrderBuilder/Checkout/Checkout';
import ManageOrders from './pages/OrderHub/ManageOrders/ManageOrders';
import OrderDetails from './pages/OrderHub/ManageOrders/Details/Details';
import OrderDesk from './pages/OrderHub/OrderDesk/OrderDesk';

// 404
import NotFound from './pages/NotFound/NotFound';

// Define child routes as a reusable array
const appRoutes: RouteObject[] = [
  // Dashboard (always enabled)
  ...(isPageEnabled('dashboard')
    ? [
        {
          index: true,
          element: <Dashboard />,
        },
      ]
    : []),
  // Admin Panel
  {
    path: 'admin/workspace-access',
    element: <WorkspaceAccess />,
  },
  {
    path: 'admin/portal-setup',
    element: <PortalSetup />,
  },
  {
    path: 'admin/test-drive-scheduler',
    element: <TestDriveScheduler />,
  },
  {
    path: 'admin/user-management',
    element: <UserManagement />,
  },
  // Prospect Desk
  {
    path: 'prospect-desk/test-drive-bookings',
    element: <TestDriveBookings />,
  },
  {
    path: 'test-drive-calendar',
    element: <TestDriveCalendar />,
  },
  ...(isPageEnabled('prospectDesk')
    ? [
        {
          path: 'prospect-desk/prospects/:prospectId?/:view?',
          element: <Prospects />,
        },
        {
          path: 'prospect-desk/create-new-prospect',
          element: <CreateNewProspect />,
        },
      ]
    : []),
  // Order Hub
  ...(isPageEnabled('orderHub')
    ? [
        {
          path: 'order-hub/order-builder',
          element: <OrderBuilder />,
        },
        {
          path: 'order-hub/order-builder/checkout',
          element: <Checkout />,
        },
        {
          path: 'order-hub/manage-orders',
          element: <ManageOrders />,
        },
        {
          path: 'order-hub/manage-orders/:orderId',
          element: <OrderDetails />,
        },
        {
          path: 'order-hub/order-desk',
          element: <OrderDesk />,
        },
        {
          path: 'order-hub/order-desk/:importerOrderNumber',
          element: <OrderDesk />,
        },
        {
          path: 'order-hub/order-desk/:importerOrderNumber/deliveries',
          element: <OrderDesk />,
        },
        {
          path: 'order-hub/order-desk/:importerOrderNumber/:orderNumber',
          element: <OrderDesk />,
        },
      ]
    : []),
];

const routes: RouteObject[] = [
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/accept-invitation',
    element: <AcceptInvitation />,
  },
  {
    path: '/',
    element: (
      <InvitationHandler>
        <AuthGuard>
          <LayoutWrapper />
        </AuthGuard>
      </InvitationHandler>
    ),
    children: appRoutes,
  },
  // Routes with locale prefix (e.g., /en-ca, /de-de, etc.)
  {
    path: '/:locale',
    element: (
      <InvitationHandler>
        <AuthGuard>
          <LayoutWrapper />
        </AuthGuard>
      </InvitationHandler>
    ),
    children: appRoutes,
  },
  // 404 - Catch all unmatched routes
  {
    path: '*',
    element: <NotFound />,
  },
];

export const router = createBrowserRouter(routes);

export default routes;
